import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Mail, RefreshCw } from 'lucide-react';
import dataProvider from '../../authentication/dataProvider';
import { Button } from '../../components/ui/button';

interface Subscriber {
  id: string;
  _id?: string;
  email: string;
  subscribedAt?: string;
  createdAt?: string;
}

const Newsletter = () => {
  const [page, setPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(20);
  const queryClient = useQueryClient();

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['newsletter', page, perPage],
    queryFn: async () => {
      const response = await dataProvider.getList('newsletter', {
        pagination: { page, perPage },
        sort: { field: 'createdAt', order: 'DESC' },
        filter: {},
      });
      return {
        data: (response?.data || []) as Subscriber[],
        total: response?.total || 0,
      };
    },
    // Real-time: poll every 10s and on tab focus so new subscribers show up live.
    refetchInterval: 10000,
    refetchOnWindowFocus: true,
  });

  const subscribers = data?.data || [];
  const total = data?.total || 0;
  const pageCount = Math.ceil(total / perPage);

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ['newsletter'] });
    refetch();
  };

  const formatDate = (value?: string) => {
    if (!value) return '-';
    const d = new Date(value);
    return isNaN(d.getTime()) ? '-' : d.toLocaleDateString('en-GB');
  };

  if (isLoading) {
    return (
      <div className='glass-card rounded-xl flex items-center justify-center min-h-[400px]'>
        <Loader2 className='h-8 w-8 animate-spin text-primary' />
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      {/* Page Header */}
      <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4'>
        <div>
          <h1 className='text-2xl font-bold text-foreground'>Newsletter Subscribers</h1>
          <p className='text-muted-foreground'>Emails collected from the “Stay Updated” forms.</p>
        </div>
        <Button variant='outline' size='sm' onClick={handleRefresh} className='gap-2'>
          <RefreshCw className='h-4 w-4' />
          Refresh
        </Button>
      </div>

      {/* Table */}
      {subscribers.length === 0 ? (
        <div className='glass-card rounded-xl flex items-center justify-center min-h-[400px]'>
          <p className='text-center py-4 text-muted-foreground'>No subscribers yet</p>
        </div>
      ) : (
        <div className='glass-card rounded-xl overflow-x-auto'>
          <table className='w-full text-sm'>
            <thead>
              <tr className='border-b border-border text-left text-muted-foreground'>
                <th className='px-4 py-3 font-medium'>#</th>
                <th className='px-4 py-3 font-medium'>Email</th>
                <th className='px-4 py-3 font-medium'>Subscribed On</th>
              </tr>
            </thead>
            <tbody>
              {subscribers.map((sub, index) => (
                <tr key={sub.id || sub._id} className='border-b border-border/50 hover:bg-muted/5'>
                  <td className='px-4 py-3 text-muted-foreground'>
                    {(page - 1) * perPage + index + 1}
                  </td>
                  <td className='px-4 py-3 text-foreground'>
                    <span className='inline-flex items-center gap-2'>
                      <Mail className='h-4 w-4 text-primary' />
                      {sub.email}
                    </span>
                  </td>
                  <td className='px-4 py-3 text-muted-foreground'>
                    {formatDate(sub.subscribedAt || sub.createdAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      <div className='flex flex-col lg:flex-row items-center justify-between gap-4 py-4 px-4 sm:py-6 sm:px-6 border-t border-border bg-muted/5 rounded-lg'>
        <div className='flex flex-wrap items-center gap-2 text-xs sm:text-sm text-muted-foreground'>
          <span>Showing</span>
          <span className='px-2 py-1 rounded-md font-semibold bg-primary/20 text-primary'>
            {total === 0 ? 0 : (page - 1) * perPage + 1}
          </span>
          <span>to</span>
          <span className='px-2 py-1 rounded-md font-semibold bg-primary/20 text-primary'>
            {Math.min(page * perPage, total)}
          </span>
          <span>of</span>
          <span className='px-2 py-1 rounded-md font-semibold bg-primary/20 text-primary'>
            {total}
          </span>
          <span>subscribers</span>
        </div>

        <div className='flex flex-col sm:flex-row items-center gap-3 w-full lg:w-auto'>
          <div className='flex items-center gap-2 w-full sm:w-auto'>
            <label className='text-xs sm:text-sm font-medium text-muted-foreground'>Show:</label>
            <select
              className='h-8 w-16 rounded-md border px-2 text-xs sm:text-sm bg-card border-border text-foreground'
              value={perPage}
              onChange={(e) => {
                setPerPage(Number(e.target.value));
                setPage(1);
              }}
            >
              {[10, 20, 30, 50].map((value) => (
                <option key={value} value={value} className='bg-card text-foreground'>
                  {value}
                </option>
              ))}
            </select>
          </div>

          <div className='flex items-center gap-2 w-full sm:w-auto'>
            <Button
              variant='outline'
              size='sm'
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              Previous
            </Button>

            <div className='flex items-center justify-center min-w-[80px] sm:min-w-[100px] px-2 sm:px-3 py-1 rounded-md text-xs sm:text-sm font-medium bg-muted text-foreground border border-border'>
              <span className='hidden sm:inline'>
                Page {page} of {pageCount || 1}
              </span>
              <span className='sm:hidden'>
                {page}/{pageCount || 1}
              </span>
            </div>

            <Button
              variant='outline'
              size='sm'
              onClick={() => setPage((p) => (p < pageCount ? p + 1 : p))}
              disabled={page >= pageCount}
            >
              Next
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Newsletter;
